import { useDispatch } from "react-redux";
import { useEffect, useRef } from "react";
import { loginActions } from "../modules/login/reducer";
import getEnv from "../utils/getEnv";

const CHECK_TIMEOUT_MS = 10000;
const RECHECK_INTERVAL_MS = 60000;
const MIN_INTERVAL_BETWEEN_CHECKS_MS = 5000;

/**
 * Verifica conectividade real com a internet (não só rede).
 * Faz um request à API; qualquer resposta (inclusive 4xx) = online.
 * Timeout ou erro de rede = offline.
 */
async function checkRealConnectivity(): Promise<boolean> {
  try {
    const apiHost = getEnv("REACT_APP_API_HOST");
    if (!apiHost) return navigator.onLine;

    const base = apiHost.replace(/\/$/, "");
    const url = `${base}/health`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), CHECK_TIMEOUT_MS);

    await fetch(url, {
      method: "GET",
      signal: controller.signal,
      headers: { "cpf-cnpj": "" },
      mode: "cors",
      cache: "no-store",
    });

    clearTimeout(timeoutId);
    return true;
  } catch {
    return false;
  }
}

export function useChangeNetworkStatus() {
  const dispatch = useDispatch();
  const lastCheckRef = useRef(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const runCheck = async (fromEvent: "mount" | "online" | "interval"): Promise<boolean> => {
    const now = Date.now();
    if (fromEvent !== "mount" && now - lastCheckRef.current < MIN_INTERVAL_BETWEEN_CHECKS_MS) {
      return false;
    }
    lastCheckRef.current = now;

    const isOnline = await checkRealConnectivity();
    dispatch(loginActions.setNetworkStatus(isOnline));
    return isOnline;
  };

  useEffect(() => {
    const handleOffline = () => {
      dispatch(loginActions.setNetworkStatus(false));
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const handleOnline = () => {
      runCheck("online").then((isOnline) => {
        if (isOnline && !intervalRef.current) {
          intervalRef.current = setInterval(() => runCheck("interval"), RECHECK_INTERVAL_MS);
        }
      });
    };

    runCheck("mount").then((isOnline) => {
      if (isOnline && !intervalRef.current) {
        intervalRef.current = setInterval(() => runCheck("interval"), RECHECK_INTERVAL_MS);
      }
    });

    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [dispatch]);
}
